import "../css/subpage.less";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getImageUrl } from "../config";
import { fetchLocalizedJson } from "../api/fetchLocalizedJson";
import PageMeta from "../common/PageMeta";
import SubpageTitle from "../common/SubpageTitle";
import FaqStickyLink from "../common/FaqStickyLink";
import SkeletonView from "../common/SkeletonView";
import { PAGE_META } from "../seo/pageMeta";

type DevelopmentCaseDetail = {
  id: string;
  title: string;
  alt: string;
  challenges: string[];
  solutions: string[];
  results: string[];
  notes: {
    tools?: string;
    language?: string;
    environment?: string;
    duration?: string;
  };
};

function DevelopmentCaseTitle({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {index > 0 ? <br /> : null}
          {line}
        </React.Fragment>
      ))}
    </>
  );
}

function DevelopmentCaseCard({ item }: { item: DevelopmentCaseDetail }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = `development-case-panel-${item.id}`;

  return (
    <article
      className="development-case-card"
      id={`development-case-${item.id}`}
    >
      <h3 className="development-case-card__title">
        <span className="development-case-card__title-bar" aria-hidden />
        <span className="development-case-card__title-text">
          <DevelopmentCaseTitle text={item.title} />
        </span>
      </h3>
      <div className="development-case-card__media">
        <img
          src={getImageUrl(`/image/img_development${item.id}.png`)}
          alt={item.alt}
        />
      </div>
      <button
        type="button"
        className={`development-case-card__footer${open ? " development-case-card__footer--open" : ""}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="development-case-card__footer-label">{t("dev_page.view_details")}</span>
        <span className="development-case-card__chevron" aria-hidden />
      </button>
      {open ? (
        <div
          id={panelId}
          className="development-case-card__panel"
          role="region"
          aria-label={`${item.title.replace(/\n/g, "")} - ${t("dev_page.view_details")}`}
        >
          <section className="development-case-card__block development-case-card__block--challenge">
            <h4 className="development-case-card__block-heading">{t("dev_page.challenge")}</h4>
            <ul className="development-case-card__list">
              {item.challenges.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
          <section className="development-case-card__block development-case-card__block--solution">
            <h4 className="development-case-card__block-heading">{t("dev_page.solution")}</h4>
            <ul className="development-case-card__list">
              {item.solutions.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
          <section className="development-case-card__block development-case-card__block--result">
            <h4 className="development-case-card__block-heading">{t("dev_page.result")}</h4>
            <ul className="development-case-card__list">
              {item.results.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
          <section className="development-case-card__block development-case-card__block--notes">
            <h4 className="development-case-card__block-heading">{t("dev_page.notes")}</h4>
            <div className="development-case-card__notes">
              {item.notes.tools ? (
                <div className="development-case-card__notes-group">
                  <div className="development-case-card__notes-bar">{t("dev_page.tools")}</div>
                  <p className="development-case-card__notes-text">
                    {item.notes.tools}
                  </p>
                </div>
              ) : null}
              <div className="development-case-card__notes-group">
                <div className="development-case-card__notes-bar">{t("dev_page.tech")}</div>
                <p className="development-case-card__notes-text">
                  {item.notes.language}
                </p>
              </div>
              <div className="development-case-card__notes-group">
                <div className="development-case-card__notes-bar">{t("dev_page.scale")}</div>
                <p className="development-case-card__notes-text">
                  {item.notes.duration}
                </p>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </article>
  );
}

const Development: React.FC = () => {
  const { i18n } = useTranslation();
  const [cases, setCases] = useState<DevelopmentCaseDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchLocalizedJson<DevelopmentCaseDetail[]>("development_cases.json")
      .then((data) => {
        setCases(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching development cases:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [i18n.language]);

  if (loading) {
    return <SkeletonView />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="subpage subpage--development">
      <PageMeta path="/development" {...PAGE_META["/development"]} />
      <SubpageTitle titleJa="開発事例" titleEn="development" as="h1" />

      <FaqStickyLink />
      <section
        className="subpage-section development-cases"
        aria-label="開発事例一覧"
      >
        <div className="development-cases__grid">
          {cases.map((item) => (
            <DevelopmentCaseCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Development;
